import React, { useState, useMemo } from 'react';

import styled from 'styled-components';

import { useUserPermission } from '@contexts/UserContext';

import ContactConnectionDrawer from '@components/Connection/ContactConnectionDrawer';
import { NwButton } from '@components/Gui/NwButton';
import { PhoneLine, MailLine, ContactAddressLine } from '@components/Gui/NwContactInfo';
import NwIcon from '@components/Gui/NwIcon';
import PermissionBlock from '@components/PermissionBlock/PermissionBlock';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const HeaderBodyContactsContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: flex-end;
    margin: 0.25rem 0.75rem 0 0;
    gap: 0.75rem;
    width: 100%;

    @media only screen and (max-width: 1440px) {
        margin: 0.5rem 0 0 0;
    }

    .header-body-contacts-box {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0 2rem;
        width: 100%;

        .header-body-contacts-lines-container {
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            justify-content: center;
            flex: 1 1 0;
            border-right: ${(props) => props.theme.DefaultBorder};
            padding-right: 2rem;

            @media only screen and (max-width: 1600px) {
                align-items: flex-start;
            }

            .header-body-contacts-lines-container-phone {
                display: flex;
                align-items: flex-end;
                justify-content: center;
                gap: 1rem;
            }

            .line-container-left {
                font-size: 1rem;
                padding-bottom: 0.1rem;

                p {
                    font-size: 1.1rem;
                    font-weight: 600;
                    /* cursor: pointer; */

                    @media only screen and (max-width: 1600px) {
                        font-size: 0.9rem;
                    }
                }

                &.disabled {
                    p {
                        font-weight: 400;
                    }
                }
            }
        }

        .header-body-contacts-edit-trigger-container {
            font-size: 1.75rem;
            padding-right: 2rem;
        }
    }

    &.in-footer {
        margin: 0 0 0 14rem;
        width: 100%;
        .header-body-contacts-box {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            font-size: 1.25rem;
            gap: 1rem;
            width: auto !important;

            .header-body-contacts-lines-container {
                display: flex;
                flex-direction: row;
                align-items: flex-end;
                justify-content: center;
                gap: 2rem;
                padding-top: 0.15rem;

                .line-container-left {
                    font-size: 1rem;

                    p {
                        font-size: 1.1rem;
                        font-weight: 600;
                        cursor: pointer;

                        @media only screen and (max-width: 1600px) {
                            font-size: 0.9rem;
                        }
                    }

                    &.line-container-left-phone,
                    &.line-container-left-mail {
                        p {
                            text-overflow: visible;
                            line-height: 1.25rem !important;
                            max-width: none;
                            font-size: 1rem !important;
                        }
                    }
                }
            }

            .header-body-contacts-edit-trigger-container {
                padding-right: 1rem;
            }
        }
    }

    &.in-drawer {
        margin: 1rem 0 0 0;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;

        .header-body-contacts-box {
            .header-body-contacts-lines-container {
                align-items: flex-start;
                .line-container-left {
                    p {
                        font-size: 0.9rem;
                    }
                }
            }
        }
    }

    &.absolute {
        display: block;
        .header-body-contacts-box {
            flex-direction: column;
            align-items: flex-end;
            .header-body-contacts-lines-container {
                gap: 0.25rem;
                border-right: none;
                padding-right: 0;

                .address-line-container {
                    .address-line-content {
                        text-align: right;
                    }
                }
            }

            .header-body-contacts-edit-trigger-container {
                padding-right: 0;
            }
        }
    }
`;

const ContactDetailsHeaderConnections = ({ contact, readOnly, inFooter, inDrawer, absolute }) => {
    const [drawer, setDrawer] = useState(false);

    const viewClientConnections = useUserPermission(
        'ViewClientConnections',
        contact && contact.Users.map((user) => user.UserID)
    );
    const viewAgencyConnections = useUserPermission(
        'ViewAgencyConnections',
        contact && contact.Users.map((user) => user.UserID)
    );
    const viewServiceConnections = useUserPermission(
        'ViewServiceConnections',
        contact && contact.Users.map((user) => user.UserID)
    );
    const viewModelConnections = useUserPermission(
        'ViewModelConnections',
        contact && contact.Users.map((user) => user.UserID)
    );

    // classNames for inFooter, inDrawer, compact
    const classNames = useMemo(() => {
        if (inFooter) return 'in-footer';
        if (inDrawer) return 'in-drawer';
        if (absolute) return 'absolute';
        return '';
    }, [inFooter, inDrawer, absolute]);

    const viewConnections = useMemo(() => {
        if (contact) {
            if (contact.SubType === 1) return viewClientConnections;
            if (contact.SubType === 2) return viewAgencyConnections;
            if (contact.SubType === 3) return viewServiceConnections;
            return viewModelConnections;
        }
        return false;
    }, [contact, viewClientConnections, viewAgencyConnections, viewServiceConnections, viewModelConnections]);

    // const defaultConnectionAddress = useMemo(() => {
    //     const address = {
    //         Street: contact.DefaultConnection.Street,
    //         City: contact.DefaultConnection.City,
    //         State: contact.DefaultConnection.State,
    //         Zip: contact.DefaultConnection.Zip,
    //         Country: contact.DefaultConnection.Country,
    //     };
    //     if (contact.DefaultConnection) {
    //         return address;
    //     }
    //     return null;
    // }, [contact.DefaultConnection]);

    return (
        <>
            <HeaderBodyContactsContainer className={classNames}>
                <PermissionBlock
                    permission={viewConnections}
                    content={
                        <div className="header-body-contacts-box">
                            <div className="header-body-contacts-lines-container">
                                <div className="header-body-contacts-lines-container-phone">
                                    <PhoneLine
                                        noIcon
                                        phoneNumber={
                                            contact.DefaultConnection && contact.DefaultConnection.Mobile
                                                ? contact.DefaultConnection.Mobile
                                                : null
                                        }
                                        phoneType="Mobile"
                                    />
                                    <PhoneLine
                                        noIcon
                                        phoneNumber={
                                            contact.DefaultConnection && contact.DefaultConnection.Phone
                                                ? contact.DefaultConnection.Phone
                                                : null
                                        }
                                        phoneType="Land"
                                    />
                                </div>
                                <MailLine
                                    groupCC
                                    noIcon
                                    mailAddress={
                                        contact.DefaultConnection && contact.DefaultConnection.Email
                                            ? contact.DefaultConnection.Email
                                            : null
                                    }
                                    mailCC={
                                        contact.DefaultConnection && contact.DefaultConnection.CC
                                            ? contact.DefaultConnection.CC
                                            : null
                                    }
                                />
                                {!inFooter && (
                                    <ContactAddressLine
                                        address={contact.DefaultConnection}
                                        compact
                                        showAccess={false}
                                    />
                                )}
                            </div>
                            <div className="header-body-contacts-edit-trigger-container">
                                {absolute ? (
                                    <NwButton
                                        ghost
                                        icon={byPrefixAndName.fasl['address-book']}
                                        label="Edit"
                                        onClick={() => viewConnections && setDrawer(true)}
                                    />
                                ) : (
                                    <NwIcon
                                        pointer
                                        onClick={() => viewConnections && setDrawer(true)}
                                        icon={byPrefixAndName.fasl['address-book']}
                                        size="1x"
                                    />
                                )}
                            </div>
                        </div>
                    }
                />
            </HeaderBodyContactsContainer>
            {drawer && (
                <ContactConnectionDrawer
                    contactSubType={contact.SubType}
                    contactId={contact.ID}
                    readOnly={readOnly}
                    onClose={() => setDrawer(false)}
                />
            )}
        </>
    );
};

export default ContactDetailsHeaderConnections;
